<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import KanbanAPI from 'dashboard/api/kanban';
import ConversationAPI from 'dashboard/api/inbox/conversation';
import PropertyVisitsAPI from 'dashboard/api/propertyVisits';
import ScheduleModal from 'dashboard/routes/dashboard/kanban/agenda/ScheduleModal.vue';
import LeadAppointmentsAPI from 'dashboard/api/leadAppointments';
import { sendAppointmentNotice } from 'dashboard/routes/dashboard/kanban/agenda/appointmentNotice';
import { emitter } from 'shared/helpers/mitt';
import ChipPopover from './ChipPopover.vue';
import PinAction from 'dashboard/components/widgets/conversation/header/PinAction.vue';
import {
  formatAppointmentChip,
  formatAppointmentRow,
  formatHourLabel,
  startOfDay,
} from './helpers/appointments';
import {
  AGENDA_ENTRY_TYPES,
  extractScheduledMessages,
  mergeAgendaEntries,
  pickNextAgendaEntry,
} from './helpers/agendaEntries';

const props = defineProps({
  conversation: { type: Object, default: () => ({}) },
  open: { type: Boolean, default: false },
  compact: { type: Boolean, default: false },
  // SiteUp: so o trilho oferece fixar; no header antigo a acao nao existe.
  pinnable: { type: Boolean, default: false },
  // Fixado no topo (V3): forma expandida, proximo evento com data e titulo.
  pinned: { type: Boolean, default: false },
  // SiteUp: no header desktop a Agenda é ação (ícone leve), não pílula de estado.
  variant: {
    type: String,
    default: 'chip',
    validator: value => ['chip', 'icon'].includes(value),
  },
});

const emit = defineEmits(['toggle', 'close', 'scheduleMessage']);

const isHeaderIcon = computed(() => props.variant === 'icon');
const triggerClass = computed(() => {
  if (isHeaderIcon.value) {
    return [
      'relative flex shrink-0 items-center justify-center rounded-full text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-slate-11',
      props.compact ? 'size-6' : 'size-7',
    ];
  }
  return 'relative flex size-10 shrink-0 items-center justify-center rounded-full bg-n-alpha-2 text-n-slate-12 hover:bg-n-alpha-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-slate-11 sm:size-8';
});
const iconClass = computed(() => {
  if (isHeaderIcon.value) return props.compact ? 'size-3.5' : 'size-4';
  return 'size-[18px] shrink-0 sm:size-4';
});
const { t } = useI18n();
const router = useRouter();
const store = useStore();

const items = ref([]);
const scheduledMessages = ref([]);
const saving = ref(false);
const editingId = ref(null);
const cancelingId = ref(null);
// Registrar visita (mockup .design-panels/lia/visita-no-chip-agenda.html):
// so para compromisso que ja passou; grava no PropertyFollowupEvent pelo
// codigo do imovel e marca o card, para o estado "registrado" sobreviver.
const visitandoId = ref(null);
const visitaResultado = ref('attended');
const visitaCodigo = ref('');
const visitaTexto = ref('');
const visitaErro = ref('');
const appointmentOpen = ref(false);
const appointmentItemId = ref(null);
const appointmentFunnelId = ref(null);
const appointmentScheduledAt = ref('');
// Formulario unico (decisao 18): a conversa nao cria mais cartao escondido.
const scheduleOpen = ref(false);
const scheduleSaving = ref(false);
const scheduleError = ref('');
const scheduleEditing = ref(null);
const scheduleCardId = ref(null);
const selectedCreateType = ref('');
let requestVersion = 0;
let unmounted = false;

const conversationId = computed(
  () => props.conversation?.id || props.conversation?.display_id
);
const contactName = computed(
  () =>
    props.conversation?.meta?.sender?.name ||
    t('CONVERSATION.HEADER.CONTEXT_CHIPS.KANBAN.UNTITLED')
);
const agents = computed(() => store.getters['agents/getAgents'] || []);

const extractItems = payload => {
  if (Array.isArray(payload)) return payload;
  return payload?.items || payload?.payload || payload?.kanban_items || [];
};

const isCurrentRequest = (version, requestedConversationId) =>
  !unmounted &&
  requestVersion === version &&
  conversationId.value === requestedConversationId;

const closeAppointmentModal = () => {
  appointmentOpen.value = false;
  appointmentItemId.value = null;
  appointmentFunnelId.value = null;
  appointmentScheduledAt.value = '';
  scheduleOpen.value = false;
  scheduleEditing.value = null;
  scheduleCardId.value = null;
  scheduleError.value = '';
};

const visibleEntries = computed(() =>
  mergeAgendaEntries({
    appointments: items.value,
    messages: scheduledMessages.value,
    contactName: contactName.value,
    agents: agents.value,
  })
);
const nextEntry = computed(() => pickNextAgendaEntry(visibleEntries.value));

const chipLabel = computed(() => {
  if (!nextEntry.value) {
    return t('CONVERSATION.HEADER.CONTEXT_CHIPS.AGENDA.SCHEDULE');
  }
  return formatAppointmentChip(nextEntry.value.iso);
});

const pinnedLabel = computed(() => {
  if (!nextEntry.value) return chipLabel.value;
  if (props.compact || !nextEntry.value.title) return chipLabel.value;
  return `${chipLabel.value} · ${nextEntry.value.title}`;
});

const chipBadge = computed(() => {
  if (!nextEntry.value?.iso) return '';
  const date = new Date(nextEntry.value.iso);
  if (!Number.isFinite(date.getTime())) return '';
  const today = startOfDay(new Date());
  const target = startOfDay(date);
  const dayDiff = Math.round((target - today) / 86400000);
  if (dayDiff === 0) return formatHourLabel(date);
  return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}`;
});

const isAppointment = entry => entry?.type === AGENDA_ENTRY_TYPES.APPOINTMENT;
const jaPassou = entry => {
  const ms = entry?.at instanceof Date ? entry.at.getTime() : Number(entry?.at);
  return Number.isFinite(ms) && ms > 0 && ms <= Date.now();
};
const visitaDe = entry => entry?.item?.item_details?.visita || null;
const podeRegistrarVisita = entry =>
  isAppointment(entry) && jaPassou(entry) && !visitaDe(entry);
const startVisita = entry => {
  editingId.value = null;
  cancelingId.value = null;
  visitandoId.value = entry.key;
  visitaResultado.value = 'attended';
  visitaCodigo.value = entry.item?.item_details?.codigo_imovel || '';
  visitaTexto.value = '';
  visitaErro.value = '';
};
const confirmVisita = async entry => {
  if (saving.value) return;
  const codigo = visitaCodigo.value.trim();
  if (!codigo) {
    visitaErro.value = t(
      'CONVERSATION.HEADER.CONTEXT_CHIPS.AGENDA.VISIT_CODE_REQUIRED'
    );
    return;
  }
  saving.value = true;
  visitaErro.value = '';
  try {
    const base = {
      codigo,
      contact_id: props.conversation?.meta?.sender?.id,
      conversation_id: props.conversation?.display_id,
      kanban_item_id: entry.item?.id,
    };
    await PropertyVisitsAPI.registrar({
      ...base,
      tipo: 'visita',
      em: entry.iso,
      resultado: visitaResultado.value,
    });
    const texto = visitaTexto.value.trim();
    if (texto)
      await PropertyVisitsAPI.registrar({ ...base, tipo: 'feedback', texto });
    const visita = {
      em: entry.iso,
      resultado: visitaResultado.value,
      codigo,
      feedback: texto || null,
    };
    // skip_appointment_emit: o item_details vai inteiro (com scheduled_at) e sem a
    // flag o KanbanAPI reemite "compromisso agendado" e abre o modal (QA v973).
    const { data } = await KanbanAPI.update(entry.item.id, {
      skip_appointment_emit: true,
      item_details: {
        ...(entry.item.item_details || {}),
        codigo_imovel: codigo,
        visita,
      },
    });
    const next = data || {
      ...entry.item,
      item_details: {
        ...(entry.item.item_details || {}),
        codigo_imovel: codigo,
        visita,
      },
    };
    items.value = items.value.map(item =>
      item.id === entry.id ? { ...item, ...next } : item
    );
    emitter.emit('kanban.item.updated', next);
    visitandoId.value = null;
    emitter.emit('newToastMessage', {
      message: t('CONVERSATION.HEADER.CONTEXT_CHIPS.AGENDA.VISIT_SAVED'),
      type: 'success',
    });
  } catch {
    visitaErro.value = t(
      'CONVERSATION.HEADER.CONTEXT_CHIPS.AGENDA.VISIT_FAILED'
    );
  } finally {
    saving.value = false;
  }
};

const fetchItems = async () => {
  if (!conversationId.value) {
    items.value = [];
    return;
  }
  const requestedConversationId = conversationId.value;
  const version = requestVersion;
  const cached = props.conversation?.kanban_items;
  if (Array.isArray(cached)) items.value = cached;
  try {
    const { data } = await KanbanAPI.getItemsByConversation(
      requestedConversationId
    );
    if (!isCurrentRequest(version, requestedConversationId)) return;
    items.value = extractItems(data);
  } catch {
    if (!isCurrentRequest(version, requestedConversationId)) return;
    if (!items.value.length) items.value = [];
  }
};

const fetchScheduledMessages = async () => {
  if (!conversationId.value) {
    scheduledMessages.value = [];
    return;
  }
  const requestedConversationId = conversationId.value;
  const version = requestVersion;
  try {
    const { data } = await ConversationAPI.getScheduledMessages(
      requestedConversationId
    );
    if (!isCurrentRequest(version, requestedConversationId)) return;
    scheduledMessages.value = extractScheduledMessages(data);
  } catch {
    if (!isCurrentRequest(version, requestedConversationId)) return;
    if (!scheduledMessages.value.length) scheduledMessages.value = [];
  }
};

const fetchAgenda = async () => {
  await Promise.all([fetchItems(), fetchScheduledMessages()]);
};

// Decisao 18: "Novo compromisso" abre o formulario unico. Se a conversa ja tem
// cartao ligado, usa ele; se nao tem, o compromisso nasce so com o contato e o
// operador pode ligar a um cartao na linha opcional do formulario. O
// ensureCard() silencioso (que criava um cartao escondido no primeiro funil so
// para poder agendar) morreu aqui.
const startNew = () => {
  editingId.value = null;
  cancelingId.value = null;
  scheduleEditing.value = null;
  scheduleError.value = '';
  scheduleCardId.value = items.value[0]?.id || null;
  scheduleOpen.value = true;
};

// Funil do cartao ligado. E o que o backend usa para achar a conexao do Google,
// entao e o que a linha de destino do formulario precisa para ser verdadeira.
const scheduleFunnelId = computed(() => {
  const card = items.value.find(
    entry => String(entry?.id) === String(scheduleCardId.value)
  );
  return card?.funnel_id ?? null;
});

// Cartoes daquele contato, para a linha "Ligar a um cartao".
const cardOptions = computed(() =>
  items.value
    .filter(entry => entry?.id)
    .map(entry => ({
      id: entry.id,
      title:
        entry.item_details?.title ||
        entry.title ||
        t('CONVERSATION.HEADER.CONTEXT_CHIPS.KANBAN.UNTITLED'),
    }))
);

const submitSchedule = async payload => {
  if (scheduleSaving.value) return;
  scheduleSaving.value = true;
  scheduleError.value = '';
  const version = requestVersion;
  const requestedConversationId = conversationId.value;
  try {
    const body = {
      scheduled_at: payload.scheduledAt,
      duration_min: payload.durationMin,
      description: payload.title,
      location: payload.location,
      responsible_user_id: payload.responsibleId,
      contact_id: props.conversation?.meta?.sender?.id || undefined,
    };
    if (payload.kanbanItemId) body.linked_kanban_item_id = payload.kanbanItemId;
    // Destino do Google so pode ser escolhido na criacao; no update o backend
    // recusa transferencia (reject_destination_transfer).
    if (
      payload.googleCalendarConnectionId &&
      !scheduleEditing.value?.appointmentId
    ) {
      body.google_calendar_connection_id = payload.googleCalendarConnectionId;
    }
    if (scheduleEditing.value?.appointmentId) {
      await LeadAppointmentsAPI.update(
        scheduleEditing.value.appointmentId,
        body
      );
    } else {
      await LeadAppointmentsAPI.create(body);
    }
    await sendAppointmentNotice(payload);
    if (!isCurrentRequest(version, requestedConversationId)) return;
    scheduleOpen.value = false;
    scheduleEditing.value = null;
    await fetchAgenda();
    emitter.emit('newToastMessage', {
      message: t('CONVERSATION.HEADER.CONTEXT_CHIPS.AGENDA.SAVED'),
      type: 'success',
    });
  } catch {
    if (!isCurrentRequest(version, requestedConversationId)) return;
    scheduleError.value = t('KANBAN.AGENDA_BOARD.SAVE_ERROR');
  } finally {
    scheduleSaving.value = false;
  }
};

const typeLabel = type =>
  type === AGENDA_ENTRY_TYPES.SCHEDULED_MESSAGE
    ? t('CONVERSATION.HEADER.CONTEXT_CHIPS.AGENDA.TYPE_MESSAGE')
    : t('CONVERSATION.HEADER.CONTEXT_CHIPS.AGENDA.TYPE_APPOINTMENT');

const typeIconClass = type =>
  type === AGENDA_ENTRY_TYPES.SCHEDULED_MESSAGE
    ? 'i-ri-chat-1-line'
    : 'i-ri-calendar-event-line';

const isCreateTypeSelected = type => selectedCreateType.value === type;

const chooseCreateType = async type => {
  selectedCreateType.value = type;
  if (type === AGENDA_ENTRY_TYPES.SCHEDULED_MESSAGE) {
    emit('scheduleMessage');
    emit('close');
    return;
  }
  emit('close');
  await startNew();
};

// Decisao 18: a edicao de data no chip deixa de ser um campo solto e passa a
// abrir o mesmo formulario unico, ja preenchido.
const startEdit = entry => {
  if (!isAppointment(entry)) return;
  editingId.value = null;
  cancelingId.value = null;
  const details = entry.item?.item_details || {};
  scheduleEditing.value = {
    appointmentId: details.lead_appointment_id || null,
    item: {
      id: entry.item?.id || null,
      kind: 'kanban_item',
      title: details.title || entry.item?.title || '',
      scheduled: entry.iso ? new Date(entry.iso) : null,
      startMin: null,
      durationMin: details.duration_min || details.duration_minutes || null,
      location: details.location || '',
      responsibleId: null,
      contact: props.conversation?.meta?.sender || null,
      kanban: { item_id: entry.item?.id || null },
    },
  };
  scheduleCardId.value = entry.item?.id || null;
  scheduleError.value = '';
  scheduleOpen.value = true;
};

const cancelOnly = async entry => {
  if (!isAppointment(entry) || !entry?.item?.id || saving.value) return;
  saving.value = true;
  try {
    const { data } = await KanbanAPI.update(entry.item.id, {
      item_details: {
        ...(entry.item.item_details || {}),
        scheduling_type: null,
        scheduled_at: null,
        deadline_at: null,
      },
    });
    const next = data || {
      ...entry.item,
      item_details: {
        ...(entry.item.item_details || {}),
        scheduled_at: null,
        scheduling_type: null,
      },
    };
    items.value = items.value.map(item =>
      item.id === entry.id ? { ...item, ...next } : item
    );
    emitter.emit('kanban.item.updated', next);
    cancelingId.value = null;
  } catch {
    // keep confirm
  } finally {
    saving.value = false;
  }
};

watch(
  () => conversationId.value,
  (nextConversationId, previousConversationId) => {
    if (nextConversationId !== previousConversationId) {
      requestVersion += 1;
      items.value = [];
      scheduledMessages.value = [];
      closeAppointmentModal();
    }
    fetchAgenda();
  },
  { immediate: true }
);

onBeforeUnmount(() => {
  unmounted = true;
  requestVersion += 1;
});

watch(
  () => props.open,
  open => {
    if (open) fetchAgenda();
    else {
      editingId.value = null;
      cancelingId.value = null;
    }
  }
);

// Atalho pra tela cheia da Agenda (o popover mostra só o desta conversa).
const openAgendaScreen = () => {
  router.push({ name: 'kanban_board', query: { view: 'agenda' } });
  emit('close');
};
</script>

<template>
  <ChipPopover :open="open" @close="emit('close')">
    <template #trigger>
      <button
        v-if="pinned"
        type="button"
        data-testid="context-chip-agenda"
        data-variant="pinned"
        class="flex h-8 max-w-[260px] items-center gap-2 rounded-full border border-n-weak bg-n-solid-1 px-2.5 text-left hover:bg-n-solid-2"
        :class="{ 'bg-n-solid-2': open }"
        :aria-expanded="open"
        :aria-label="pinnedLabel"
        :title="pinnedLabel"
        @click.stop="emit('toggle')"
      >
        <span class="i-ri-calendar-line size-4 shrink-0" aria-hidden="true" />
        <span class="min-w-0 truncate text-[12.5px] font-bold text-n-slate-12">
          {{ pinnedLabel }}
        </span>
      </button>
      <button
        v-else
        v-tooltip.bottom="chipLabel"
        type="button"
        data-testid="context-chip-agenda"
        :data-variant="variant"
        :class="triggerClass"
        :aria-expanded="open"
        :aria-label="chipLabel"
        @click.stop="emit('toggle')"
      >
        <span
          class="i-ri-calendar-line shrink-0"
          :class="iconClass"
          aria-hidden="true"
        />
        <span
          v-if="chipBadge"
          class="pointer-events-none absolute -right-0.5 -top-0.5 inline-flex h-3.5 min-w-3.5 items-center justify-center whitespace-nowrap rounded-full bg-n-slate-12 px-0.5 font-bold tabular-nums leading-none text-n-solid-1"
          :class="compact ? 'text-[8px]' : 'text-[9px]'"
          aria-hidden="true"
        >
          {{ chipBadge }}
        </span>
      </button>
    </template>

    <p class="text-[14px] font-medium text-n-slate-12">
      {{ t('CONVERSATION.HEADER.CONTEXT_CHIPS.AGENDA.TITLE') }}
    </p>
    <p
      class="mt-3 text-[11px] font-medium uppercase tracking-wide text-n-slate-11"
      data-testid="agenda-choose-label"
    >
      {{ t('CONVERSATION.HEADER.CONTEXT_CHIPS.AGENDA.CHOOSE_TYPE') }}
    </p>

    <div
      class="mt-2 flex gap-1"
      role="radiogroup"
      data-testid="agenda-choose-type"
      :aria-label="t('CONVERSATION.HEADER.CONTEXT_CHIPS.AGENDA.CHOOSE_TYPE')"
    >
      <button
        type="button"
        role="radio"
        data-testid="agenda-choose-appointment"
        class="inline-flex h-8 flex-1 items-center justify-center gap-1 rounded-full px-2 text-[12px] font-bold"
        :class="
          isCreateTypeSelected(AGENDA_ENTRY_TYPES.APPOINTMENT)
            ? 'bg-n-slate-12 text-n-solid-1'
            : 'bg-n-alpha-2 text-n-slate-11'
        "
        :aria-checked="isCreateTypeSelected(AGENDA_ENTRY_TYPES.APPOINTMENT)"
        @click="chooseCreateType(AGENDA_ENTRY_TYPES.APPOINTMENT)"
      >
        <span
          class="i-ri-calendar-event-line size-3.5 shrink-0"
          aria-hidden="true"
        />
        {{ t('CONVERSATION.HEADER.CONTEXT_CHIPS.AGENDA.TYPE_APPOINTMENT') }}
      </button>
      <button
        type="button"
        role="radio"
        data-testid="agenda-choose-message"
        class="inline-flex h-8 flex-1 items-center justify-center gap-1 rounded-full px-2 text-[12px] font-bold"
        :class="
          isCreateTypeSelected(AGENDA_ENTRY_TYPES.SCHEDULED_MESSAGE)
            ? 'bg-n-slate-12 text-n-solid-1'
            : 'bg-n-alpha-2 text-n-slate-11'
        "
        :aria-checked="
          isCreateTypeSelected(AGENDA_ENTRY_TYPES.SCHEDULED_MESSAGE)
        "
        @click="chooseCreateType(AGENDA_ENTRY_TYPES.SCHEDULED_MESSAGE)"
      >
        <span class="i-ri-chat-1-line size-3.5 shrink-0" aria-hidden="true" />
        {{ t('CONVERSATION.HEADER.CONTEXT_CHIPS.AGENDA.TYPE_MESSAGE') }}
      </button>
    </div>

    <ul
      v-if="visibleEntries.length"
      class="mt-3 space-y-2"
      data-testid="agenda-entries"
    >
      <li
        v-for="entry in visibleEntries"
        :key="entry.key"
        class="rounded-xl border border-n-weak px-2.5 py-2"
        :data-entry-type="entry.type"
        :data-testid="`agenda-entry-${entry.type}-${entry.id}`"
      >
        <div class="flex items-start gap-2">
          <div class="min-w-0 flex-1">
            <div class="flex min-w-0 items-center gap-1.5">
              <span
                class="inline-flex shrink-0 items-center gap-1 rounded-full bg-n-alpha-2 px-1.5 py-0.5 text-[10px] font-bold text-n-slate-11"
              >
                <span
                  class="size-3 shrink-0"
                  :class="typeIconClass(entry.type)"
                  aria-hidden="true"
                />
                {{ typeLabel(entry.type) }}
              </span>
              <p
                class="min-w-0 truncate text-[13px] font-bold tabular-nums text-n-slate-12"
              >
                {{ formatAppointmentRow(entry.iso) }}
              </p>
            </div>
            <p class="mt-1 truncate text-[12px] text-n-slate-11">
              {{ entry.title }}
            </p>
            <p
              v-if="entry.author"
              class="truncate text-[12px] text-n-slate-10"
              data-testid="agenda-entry-author"
            >
              {{ entry.author }}
            </p>
            <p
              v-if="entry.recurrent"
              class="mt-0.5 inline-flex items-center gap-1 text-[11px] font-bold text-n-slate-11"
              data-testid="agenda-entry-recurrent"
            >
              <span class="i-ri-repeat-2-line size-3" aria-hidden="true" />
              {{ t('CONVERSATION.HEADER.CONTEXT_CHIPS.AGENDA.RECURRENT') }}
            </p>
          </div>
          <template v-if="isAppointment(entry)">
            <button
              v-if="podeRegistrarVisita(entry)"
              type="button"
              class="flex size-8 shrink-0 items-center justify-center rounded-full bg-n-teal-9/15 text-n-teal-11 hover:bg-n-teal-9/25"
              :aria-label="
                t('CONVERSATION.HEADER.CONTEXT_CHIPS.AGENDA.VISIT_ACTION')
              "
              :data-testid="`agenda-visit-${entry.id}`"
              @click="startVisita(entry)"
            >
              <span class="i-ri-check-line size-4" />
            </button>
            <button
              type="button"
              class="flex size-8 shrink-0 items-center justify-center rounded-full hover:bg-n-alpha-2"
              :aria-label="
                t('CONVERSATION.HEADER.CONTEXT_CHIPS.AGENDA.RESCHEDULE')
              "
              @click="startEdit(entry)"
            >
              <span class="i-ri-pencil-line size-4" />
            </button>
            <button
              type="button"
              class="flex size-8 shrink-0 items-center justify-center rounded-full hover:bg-n-alpha-2"
              :aria-label="t('CONVERSATION.HEADER.CONTEXT_CHIPS.AGENDA.CANCEL')"
              @click="cancelingId = entry.key"
            >
              <span class="i-ri-close-line size-4" />
            </button>
          </template>
        </div>

        <div
          v-if="visitaDe(entry)"
          class="mt-2 text-[12px]"
          :data-testid="`agenda-visit-done-${entry.id}`"
        >
          <p
            class="m-0 inline-flex items-center gap-1 font-bold text-n-teal-11"
          >
            <span class="i-ri-check-line size-3.5" aria-hidden="true" />
            {{
              visitaDe(entry).resultado === 'no_show'
                ? t(
                    'CONVERSATION.HEADER.CONTEXT_CHIPS.AGENDA.VISIT_NO_SHOW_DONE',
                    { codigo: visitaDe(entry).codigo }
                  )
                : t('CONVERSATION.HEADER.CONTEXT_CHIPS.AGENDA.VISIT_DONE', {
                    codigo: visitaDe(entry).codigo,
                  })
            }}
          </p>
          <p
            v-if="visitaDe(entry).feedback"
            class="m-0 mt-1 border-l-2 border-n-teal-9 pl-2 text-n-slate-11"
          >
            {{ visitaDe(entry).feedback }}
          </p>
          <p class="m-0 mt-1 text-[11px] text-n-slate-10">
            {{ t('CONVERSATION.HEADER.CONTEXT_CHIPS.AGENDA.VISIT_HINT') }}
          </p>
        </div>
        <div
          v-if="visitandoId === entry.key"
          class="mt-2"
          :data-testid="`agenda-visit-form-${entry.id}`"
        >
          <div class="flex gap-1.5" role="radiogroup">
            <button
              type="button"
              role="radio"
              class="inline-flex h-8 flex-1 items-center justify-center rounded-full border text-[12px] font-bold"
              :class="
                visitaResultado === 'attended'
                  ? 'border-n-teal-9 bg-n-teal-9 text-white'
                  : 'border-n-weak text-n-slate-11'
              "
              :aria-checked="visitaResultado === 'attended'"
              @click="visitaResultado = 'attended'"
            >
              {{ t('CONVERSATION.HEADER.CONTEXT_CHIPS.AGENDA.VISIT_HAPPENED') }}
            </button>
            <button
              type="button"
              role="radio"
              class="inline-flex h-8 flex-1 items-center justify-center rounded-full border text-[12px] font-bold"
              :class="
                visitaResultado === 'no_show'
                  ? 'border-n-amber-9 bg-n-amber-9 text-white'
                  : 'border-n-weak text-n-slate-11'
              "
              :aria-checked="visitaResultado === 'no_show'"
              @click="visitaResultado = 'no_show'"
            >
              {{ t('CONVERSATION.HEADER.CONTEXT_CHIPS.AGENDA.VISIT_NO_SHOW') }}
            </button>
          </div>
          <label
            class="mt-2 flex items-center gap-2 text-[12px] text-n-slate-11"
          >
            {{ t('CONVERSATION.HEADER.CONTEXT_CHIPS.AGENDA.VISIT_PROPERTY') }}
            <input
              v-model="visitaCodigo"
              type="text"
              class="h-8 w-28 rounded-full border border-n-weak bg-n-solid-1 px-3 font-mono text-[12px] text-n-slate-12"
              :placeholder="
                t(
                  'CONVERSATION.HEADER.CONTEXT_CHIPS.AGENDA.VISIT_CODE_PLACEHOLDER'
                )
              "
              :data-testid="`agenda-visit-code-${entry.id}`"
            />
          </label>
          <textarea
            v-model="visitaTexto"
            rows="3"
            maxlength="1000"
            class="mt-2 w-full resize-y rounded-xl border border-n-weak bg-n-solid-1 px-2.5 py-2 text-[12.5px] text-n-slate-12"
            :placeholder="
              t(
                'CONVERSATION.HEADER.CONTEXT_CHIPS.AGENDA.VISIT_FEEDBACK_PLACEHOLDER'
              )
            "
            :data-testid="`agenda-visit-text-${entry.id}`"
          />
          <p v-if="visitaErro" class="m-0 mt-1 text-[11px] text-n-ruby-11">
            {{ visitaErro }}
          </p>
          <div class="mt-2 flex gap-2">
            <button
              type="button"
              class="inline-flex h-8 flex-1 items-center justify-center rounded-full bg-n-slate-12 px-3 text-[13px] font-bold text-n-solid-1 disabled:opacity-50"
              :disabled="saving"
              :data-testid="`agenda-visit-save-${entry.id}`"
              @click="confirmVisita(entry)"
            >
              {{ t('CONVERSATION.HEADER.CONTEXT_CHIPS.AGENDA.VISIT_SAVE') }}
            </button>
            <button
              type="button"
              class="inline-flex h-8 items-center rounded-full px-3 text-[13px] font-bold text-n-slate-11"
              @click="visitandoId = null"
            >
              {{ t('CONVERSATION.HEADER.CONTEXT_CHIPS.AGENDA.BACK') }}
            </button>
          </div>
        </div>
        <div v-if="cancelingId === entry.key" class="mt-2 space-y-1.5">
          <button
            type="button"
            disabled
            class="flex min-h-11 w-full items-center justify-center rounded-full border border-n-weak px-3 text-[13px] font-bold text-n-slate-8"
            :title="
              t('CONVERSATION.HEADER.CONTEXT_CHIPS.AGENDA.NOTIFY_UNAVAILABLE')
            "
          >
            {{
              t('CONVERSATION.HEADER.CONTEXT_CHIPS.AGENDA.CANCEL_AND_NOTIFY')
            }}
          </button>
          <button
            type="button"
            class="flex min-h-11 w-full items-center justify-center rounded-full bg-n-ruby-9/15 px-3 text-[13px] font-bold text-n-ruby-11"
            :disabled="saving"
            @click="cancelOnly(entry)"
          >
            {{ t('CONVERSATION.HEADER.CONTEXT_CHIPS.AGENDA.CANCEL_ONLY') }}
          </button>
          <button
            type="button"
            class="flex min-h-11 w-full items-center justify-center rounded-full px-3 text-[13px] font-bold text-n-slate-11"
            @click="cancelingId = null"
          >
            {{ t('CONVERSATION.HEADER.CONTEXT_CHIPS.AGENDA.BACK') }}
          </button>
        </div>
      </li>
    </ul>
    <p v-else class="mt-2 text-[13px] text-n-slate-11">
      {{ t('CONVERSATION.HEADER.CONTEXT_CHIPS.AGENDA.EMPTY') }}
    </p>

    <button
      type="button"
      class="mt-3 inline-flex h-8 w-full items-center justify-center gap-1.5 rounded-full px-3 text-[12.5px] font-bold text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12"
      @click="openAgendaScreen"
    >
      <span
        class="i-ri-external-link-line size-3.5 shrink-0"
        aria-hidden="true"
      />
      {{ t('CONVERSATION.HEADER.CONTEXT_CHIPS.AGENDA.OPEN_SCREEN') }}
    </button>

    <p class="mt-3 text-[12px] leading-snug text-n-slate-10">
      {{ t('CONVERSATION.HEADER.CONTEXT_CHIPS.AGENDA.FOOTER') }}
    </p>

    <template v-if="pinnable" #actions>
      <PinAction modulo="agenda" @done="emit('close')" />
    </template>
  </ChipPopover>
  <ScheduleModal
    v-model="scheduleOpen"
    :saving="scheduleSaving"
    :save-error="scheduleError"
    :item="scheduleEditing?.item || null"
    :items="[]"
    :agents="agents"
    :contact-name="contactName"
    :contact-phone="conversation?.meta?.sender?.phone_number || ''"
    :kanban-item-id="scheduleCardId"
    :funnel-id="scheduleFunnelId"
    :card-options="cardOptions"
    show-card-link
    elevated
    @submit="submitSchedule"
  />
</template>
